// GridControl.tsx
import React, { useState } from 'react';
import ControlRenderer, { Control } from '../ControlRenderer';
import { get, isEmpty, removeEmptyProperties, set } from '../utils/Utils';
import { FormModeType } from '@/components/FormDataProvider';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AddControlIcon from '@mui/icons-material/AddCircleOutline';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline';
import MenuIcon from '@mui/icons-material/ListAlt';
import LeftIcon from '@mui/icons-material/ArrowLeft';
import RightIcon from '@mui/icons-material/ArrowRight';
import UpIcon from '@mui/icons-material/ArrowDropUp';
import DownIcon from '@mui/icons-material/ArrowDropDown';
import { IconButton, Menu, MenuItem, Typography } from '@mui/material';
import ControlEditor, { getDefaultControl } from '../ControlEditor';
import ModalBox from '../ModalBox';
import gridPositionEditor from '../../../layouts/GridPositionEditor.json';
import gridPositionEditorRule from '../../../schema/GridPositionEditor.json';
import gridColumnEditor from '../../../layouts/GridColumnEditor.json';
import gridColumnEditorRule from '../../../schema/GridColumnEditor.json';
import gridRowEditor from '../../../layouts/GridRowEditor.json';
import gridRowEditorRule from '../../../schema/GridRowEditor.json';
import { ValidationProvider, useValidation } from '@/components/ValidationProvider';
import { getPropertyRule, Rule } from '../utils/Validation';
import RuleEditor, { getDefaultRule } from '../RuleEditor';
import { is } from 'date-fns/locale';

/**
 * Defines the attributes for a column in a grid layout.
 */
interface ColumnProps {
  /**
   * The unique key identifier for the column, used for indexing or accessing specific columns. (Optional)
   */
  key?: string;

  /**
   * The display name for the column, typically shown as a header in a table or grid. (Optional)
   */
  name?: string;

  /**
   * Specifies the width of the column using any valid CSS width value (e.g., '100px', '10%'). (Optional)
   */
  width?: string;

  /**
   * Sets the minimum width for the column to ensure it does not shrink below a certain size. (Optional)
   */
  minWidth?: string;

  /**
   * Sets the maximum width for the column, limiting how wide it can grow. (Optional)
   */
  maxWidth?: string;
}

/**
* Specifies the properties for configuring a row in a grid layout.
*/
interface RowProps {
  /**
   * Defines the height of the row, which can be specified using valid CSS height values (e.g., '50px', '20%'). (Optional)
   */
  height?: string;

  /**
   * Establishes the minimum height for the row, preventing it from shrinking below a specified size. (Optional)
   */
  minHeight?: string;

  /**
   * Limits the maximum height the row can achieve, using valid CSS height values. (Optional)
   */
  maxHeight?: string;
}

/**
* Denotes the position and span settings for a grid item.
*/
export interface GridPosition {
  /**
   * The index of the row where the item is positioned, starting from 0.
   */
  row: number;

  /**
   * The index of the column where the item is positioned, starting from 0.
   */
  column: number;

  /**
   * Specifies how many rows the item should span across. (Optional)
   */
  rowSpan?: number;

  /**
   * Specifies how many columns the item should span across. (Optional)
   */
  columnSpan?: number;
}

/**
* Defines a grid control that integrates rows and columns, extending the Control interface.
*/
export interface GridControl extends Control {
  /**
   * An array of RowProps objects defining the configuration for each row in the grid.
   */
  rows: RowProps[];

  /**
   * An array of ColumnProps objects, setting up each column's properties within the grid.
   */
  columns: ColumnProps[];

  /**
   * Determines whether the grid should be hidden when it has no content, aiding in maintaining a clean interface. (Optional)
   */
  hideWhenEmpty?: boolean;
}

interface GridControlProps {
  control: Control;
  data: any;
  onDataChange: (key: string, rowIndex: number, fieldDataKey: string, newRowData: any) => void;
  mode: FormModeType;
  onParentMenuClick?: (event: React.MouseEvent<HTMLElement>) => void; // called when a parent menu is clicked
  onComplete?: () => void;
  onError?: () => void;
  onClose?: () => void;
}

const GridControl: React.FC<GridControlProps> = ({ control, data, onDataChange, mode, onParentMenuClick, onComplete, onError, onClose }) => {

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuAnchorElAdd, setMenuAnchorElAdd] = React.useState<null | HTMLElement>(null);
  const [currentControl, setCurrentControl] = React.useState<Control | null>(null);
  const [currentRule, setCurrentRule] = React.useState<Rule | null>(null);
  const [currentRow, setCurrentRow] = React.useState<number>(0);
  const [currentColumn, setCurrentColumn] = React.useState<number>(0);
  const [editControl, setEditControl] = React.useState<Control | null>(null);
  const [editRowProps, setEditRowProps] = React.useState<RowProps | null>(null);
  const [editColumnProps, setEditColumnProps] = React.useState<ColumnProps | null>(null);
  const [editRule, setEditRule] = React.useState<Rule | null>(null);
  const [editGridPosition, setEditGridPosition] = React.useState<Control | null>(null);
  const { rule, validateData } = useValidation();

  const handleSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorElAdd(event.currentTarget);
  };

  const handleSubmenuClose = () => {
    setMenuAnchorElAdd(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, control: Control) => {
    event.stopPropagation();
    setCurrentControl(control);
    let pos = control.gridPosition;
    if (pos) {
      setCurrentRow(pos.row);
      setCurrentColumn(pos.column);
    }
    const currentRule = control.dataKey && getPropertyRule(rule, control.dataKey);
    if (currentRule) {
      setCurrentRule(currentRule);
    }
    else {
      setCurrentRule(null);
    }
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuOpenEmpty = (event: React.MouseEvent<HTMLElement>, row: number, column: number) => {
    setCurrentRow(row);
    setCurrentColumn(column);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setCurrentControl(null);
    setCurrentRule(null);
  };

  var gridContext: any;
  if (control.dataKey) {
    gridContext = get(data, control.dataKey, undefined);
    if (!gridContext) {
      gridContext = control.dataContextKey === 'FORMDATA' ? [] : {};
      set(data, control.dataKey, gridContext)
      console.log(`creating a missing datacontext ${control.dataKey}`);
    }
  }
  else {
    gridContext = data;
  }

  const gridControl = control as GridControl;

  if (gridControl.hideWhenEmpty && isEmpty(gridContext)) {
    return;
  }

  const handleEditControl = (controlToEdit: Control) => {
    setEditControl(controlToEdit);
  };

  const handleEditRule = (ruleToEdit: Rule) => {
    setEditRule(ruleToEdit);
  };

  const handleEditRuleComplete = () => {
    if (editRule) {
      // need to remove empty props to validate
      removeEmptyProperties(editRule);
    }
    setEditRule(null);
    validateData();
  };

  const handleAddRule = (control: Control) => {
    if (rule.properties && control.dataKey) {
      var newRule = getDefaultRule(control.type);
      if (newRule) {
        rule.properties[control.dataKey] = newRule;
        setEditRule(newRule);
      }
    }
  };

  const handleDeleteRule = (control: Control) => {
    if (rule.properties && control.dataKey) {
      delete rule.properties[control.dataKey];
    }
    setEditRule(null);
    validateData();
  };

  const handleEditGridPosition = (controlToEdit: Control) => {
    setEditGridPosition(controlToEdit);
  };

  const handleAddControl = (type: string) => {
    var newControl = getDefaultControl(type);
    if (currentControl) {
      // adding to control
      currentControl.controls?.push(newControl);
    } else {
      // adding to grid
      newControl.gridPosition = {
        row: currentRow,
        column: currentColumn
      };
      control.controls?.push(newControl);
    }

    setEditControl(newControl);
    setMenuAnchorEl(null);
    setMenuAnchorElAdd(null);
  };

  const handleRemoveControl = (controlToRemove: Control) => {
    // Remove the control from the grid's controls
    const updatedControls = gridControl.controls?.filter((c: Control) => c !== controlToRemove);

    // Update the grid control's controls
    // Assuming you have a setControls function or similar to update state
    set(control, 'controls', updatedControls);

    // Trigger parent update if necessary
    onDataChange(control.name, -1, '', updatedControls); // -1 for rowIndex if not applicable
  };

  const handleInsertColumn = (currentColumn: number, toLeft: boolean) => {
    gridControl.columns.push({});
    // relocate the controls
    const offset = toLeft ? 0 : 1;

    if (control.controls && currentColumn >= 0) {
      for (let i = 0; i < control.controls.length; i++) {
        const controlItem = control.controls[i];
        if (controlItem.gridPosition && controlItem.gridPosition?.column >= currentColumn + offset)
          ++controlItem.gridPosition.column;
      }
    }
  };

  const handleDeleteColumn = (currentColumn: number) => {
    gridControl.columns.pop();
    // Remove the control from the grid's controls
    const updatedControls = gridControl.controls?.filter((c: Control) => c.gridPosition?.column !== currentColumn);

    // Update the grid control's controls
    set(control, 'controls', updatedControls);

    if (control.controls && currentColumn >= 0) {
      for (let i = 0; i < control.controls.length; i++) {
        const controlItem = control.controls[i];
        if (controlItem.gridPosition && controlItem.gridPosition?.column > currentColumn)
          --controlItem.gridPosition.column;
      }
    }

    // Trigger parent update if necessary
    onDataChange(control.name, -1, '', updatedControls); // -1 for rowIndex if not applicable
  };

  const handleInsertRow = (currentRow: number, toUp: boolean) => {
    gridControl.rows.push({});
    // relocate the controls
    const offset = toUp ? 0 : 1;

    if (control.controls && currentRow >= 0) {
      for (let i = 0; i < control.controls.length; i++) {
        const controlItem = control.controls[i];
        if (controlItem.gridPosition && controlItem.gridPosition?.row >= currentRow + offset)
          ++controlItem.gridPosition.row;
      }
    }
  };

  const handleDeleteRow = (currentRow: number) => {
    gridControl.rows.pop();
    // Remove the control from the grid's controls
    const updatedControls = gridControl.controls?.filter((c: Control) => c.gridPosition?.row !== currentRow);

    // Update the grid control's controls
    set(control, 'controls', updatedControls);

    if (control.controls && currentRow >= 0) {
      for (let i = 0; i < control.controls.length; i++) {
        const controlItem = control.controls[i];
        if (controlItem.gridPosition && controlItem.gridPosition?.row > currentRow)
          --controlItem.gridPosition.row;
      }
    }

    // Trigger parent update if necessary
    onDataChange(control.name, -1, '', updatedControls); // -1 for rowIndex if not applicable
  };

  const handleEditRowProps = (currentRow: number) => {
    if (gridControl.rows[currentRow]) {
      setEditRowProps(gridControl.rows[currentRow]);
    }
  };

  const handleEditColumnProps = (currentColumn: number) => {
    if (gridControl.columns[currentColumn]) {
      setEditColumnProps(gridControl.columns[currentColumn]);
    }
  };

  const renderGridCells = () => {
    const rows = [];
    const isEmptyColumn = new Array(gridControl.columns.length).fill(true);
    for (let rowIndex = 0; rowIndex < gridControl.rows.length; rowIndex++) {
      const row = gridControl.rows[rowIndex];
      const rowKey = `row-${rowIndex}`;
      let isEmptyRow = true;

      const rowCells = [];
      for (let columnIndex = 0; columnIndex < gridControl.columns.length; columnIndex++) {
        const column = gridControl.columns[columnIndex];

        const cellStyle = generateCellStyle(column, row);
        const controlAtPosition = getControlAtPosition(rowIndex, columnIndex);
        const cellKey = `cell-${rowIndex}-${columnIndex}`;

        if (controlAtPosition) {
          isEmptyRow = false;
          isEmptyColumn[columnIndex] = false;
          const { rowSpan, colSpan } = controlAtPosition.gridPosition || {};

          if (mode === 'design') {
            cellStyle.border = '1px solid gray';
            cellStyle.position = 'relative';
          }

          const cellProps: { [key: string]: any } = { style: cellStyle };

          if (rowSpan && rowSpan > 1) {
            cellProps.rowSpan = rowSpan;
          }

          if (colSpan && colSpan > 1) {
            cellProps.colSpan = colSpan;
            columnIndex += colSpan - 1; // skip columns
          }

          if (controlAtPosition.horizontalAlignment != 'stretch') {
            cellStyle.textAlign = controlAtPosition.horizontalAlignment as "center" | "right" | "left" | undefined;
          }

          if (controlAtPosition.verticalAlignment != 'stretch') {
            cellStyle.verticalAlign = controlAtPosition.verticalAlignment as "top" | "middle" | "bottom" | undefined;
          }

          if (controlAtPosition === currentControl) {
            cellStyle.border = '1px solid red';
          }

          rowCells.push(
            <td key={cellKey} {...cellProps}>
              <ControlRenderer
                key={controlAtPosition.name}
                control={controlAtPosition}
                data={gridContext}
                onDataChange={(newRowData: any) =>
                  onDataChange(control.name, rowIndex, controlAtPosition.dataKey ?? '', newRowData)
                }
                mode={mode}
                onParentMenuClick={(event) => handleMenuOpen(event, controlAtPosition)}
                onComplete={onComplete}
                onError={onError}
                onClose={onClose}
              />
              {mode === 'design' &&
                controlAtPosition.type !== 'grid' &&
                controlAtPosition.type !== 'datagrid' &&
                controlAtPosition.type !== 'flex' &&
                (
                  <>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      display: 'flex'
                    }}>
                      <IconButton
                        style={{
                          padding: 0,
                          color: controlAtPosition.dataKey && getPropertyRule(rule, controlAtPosition.dataKey) ? 'purple' : 'gray',
                          zIndex: 1000
                        }}
                        onClick={(event) => handleMenuOpen(event, controlAtPosition)}
                      >
                        <MenuIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </>
                )
              }
            </td >
          );
        }
        else {
          if (mode === 'design' && rowIndex == gridControl.rows.length - 1 && isEmptyColumn[columnIndex]) {
            cellStyle.width = '15px'; // make it easier to click to add a new column
          }
          rowCells.push(
            <td key={cellKey} style={{ position: 'relative', ...cellStyle }}>
              {mode === 'design' && (
                <IconButton
                  style={{
                    position: 'absolute',
                    padding: 0,
                    color: 'green',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}
                  onClick={(event) => handleMenuOpenEmpty(event, rowIndex, columnIndex)}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              )
              }
            </td>
          );
        }
      }

      if (mode === 'design' && isEmptyRow) {
        // add a placeholder row to allow adding controls
        rows.push(
          <tr key={rowKey} style={{ height: '15px' }} >
            {rowCells}
          </tr>
        );
      }
      else {
        rows.push(
          <tr key={rowKey} >
            {rowCells}
          </tr>
        );
      }
    }

    return rows;
  };

  const getControlAtPosition = (rowIndex: number, columnIndex: number): Control | undefined => {
    const position: GridPosition = {
      row: rowIndex,
      column: columnIndex,
    };

    return (control.controls || []).find((c: Control) => {
      const cPosition: GridPosition = c.gridPosition || { row: 0, column: 0 };
      return (
        cPosition.row === position.row &&
        cPosition.column === position.column
      );
    });
  };

  const generateCellStyle = (column: ColumnProps, row: RowProps) => {
    const style: React.CSSProperties = {};

    if (column.width) style.width = column.width;
    if (column.minWidth) style.minWidth = column.minWidth;
    if (column.maxWidth) style.maxWidth = column.maxWidth;
    if (row.height) style.height = row.height;
    if (row.minHeight) style.minHeight = row.minHeight;
    if (row.maxHeight) style.maxHeight = row.maxHeight;

    return style;
  };

  return (
    <>
      <table className="eform-grid">
        <tbody>{renderGridCells()}</tbody>
      </table>
      {menuAnchorEl && (<Menu
        id="control-menu"
        disableScrollLock
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {onParentMenuClick &&
          <MenuItem onClick={(event) => { onParentMenuClick(event); }}>
            <MenuIcon className="eform-menu-icon" />
            <Typography variant="inherit">Parent Control</Typography>
          </MenuItem>
        }
        {currentControl && <>
          <MenuItem onClick={() => { handleEditControl(currentControl); handleMenuClose(); }}>
            <EditIcon className="eform-menu-icon" style={{ color: "green" }} />
            <Typography variant="inherit">{`Edit ${currentControl.type} Properties`}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleEditGridPosition(currentControl); handleMenuClose(); }}>
            <EditIcon className="eform-menu-icon" style={{ color: "green" }} />
            <Typography variant="inherit">{`Edit Grid Position (${currentControl.type})`}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleRemoveControl(currentControl); handleMenuClose(); }}>
            <RemoveIcon className="eform-menu-icon" style={{ color: "red" }} />
            <Typography variant="inherit">{`Remove Control (${currentControl.type})`}</Typography>
          </MenuItem>
        </>}
        {currentRule && rule?.properties && currentControl?.dataKey && <>
          <MenuItem onClick={() => { handleEditRule(currentRule); handleMenuClose(); }}>
            <EditIcon className="eform-menu-icon" style={{ color: "purple" }} />
            <Typography variant="inherit">Edit Validation</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleDeleteRule(currentControl); handleMenuClose(); }}>
            <RemoveIcon className="eform-menu-icon" style={{ color: "purple" }} />
            <Typography variant="inherit">Delete Validation</Typography>
          </MenuItem>
        </>}
        {!currentRule && rule?.properties && currentControl?.dataKey && <>
          <MenuItem onClick={() => { handleAddRule(currentControl); handleMenuClose(); }}>
            <AddControlIcon className="eform-menu-icon" style={{ color: "purple" }} />
            <Typography variant="inherit">Add Validation</Typography>
          </MenuItem>
        </>}
        {(!currentControl || currentControl.type === "tab" || currentControl.type === "flex") && <>
          <MenuItem onClick={handleSubmenuOpen}>
            <AddControlIcon className="eform-menu-icon" style={{ color: "green" }} />
            <Typography variant="inherit">{currentControl ? `Add Item to ${currentControl.type} Control` : "Add Control"}</Typography>
          </MenuItem>
          <Menu
            id="addmenu"
            anchorEl={menuAnchorElAdd}
            open={Boolean(menuAnchorElAdd)}
            onClose={handleSubmenuClose}
          >
            <MenuItem onClick={() => { handleAddControl("label") }}>
              <Typography variant="inherit">Label</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("text") }}>
              <Typography variant="inherit">Text</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("textarea") }}>
              <Typography variant="inherit">TextArea</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("select") }}>
              <Typography variant="inherit">Dropdown</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("date") }}>
              <Typography variant="inherit">DatePicker</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("check") }}>
              <Typography variant="inherit">Checkbox</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("color") }}>
              <Typography variant="inherit">Color</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("button") }}>
              <Typography variant="inherit">Button</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("grid") }}>
              <Typography variant="inherit">Grid</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("flex") }}>
              <Typography variant="inherit">FlexGrid</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("datagrid") }}>
              <Typography variant="inherit">DataGrid</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("tab") }}>
              <Typography variant="inherit">Tab Control</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("upload") }}>
              <Typography variant="inherit">Upload Control</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("map") }}>
              <Typography variant="inherit">Map Control</Typography>
            </MenuItem>
          </Menu>
        </>}
        <MenuItem onClick={() => { handleInsertColumn(currentColumn, true); handleMenuClose(); }}>
          <LeftIcon className="eform-menu-icon" />
          <Typography variant="inherit">Insert Column Left</Typography>
        </MenuItem>
        <MenuItem onClick={() => { handleInsertColumn(currentColumn, false); handleMenuClose(); }}>
          <RightIcon className="eform-menu-icon" />
          <Typography variant="inherit">Insert Column Right</Typography>
        </MenuItem>
        <MenuItem onClick={() => { handleEditColumnProps(currentColumn); handleMenuClose(); }}>
          <EditIcon className="eform-menu-icon" style={{ color: "#007bff" }} />
          <Typography variant="inherit">Edit Column Properties</Typography>
        </MenuItem>
        <MenuItem onClick={() => { handleDeleteColumn(currentColumn); handleMenuClose(); }}>
          <RemoveIcon className="eform-menu-icon" />
          <Typography variant="inherit">Delete Column</Typography>
        </MenuItem>
        <MenuItem onClick={() => { handleInsertRow(currentRow, true); handleMenuClose(); }}>
          <UpIcon className="eform-menu-icon" />
          <Typography variant="inherit">Insert Row Up</Typography>
        </MenuItem>
        <MenuItem onClick={() => { handleInsertRow(currentRow, false); handleMenuClose(); }}>
          <DownIcon className="eform-menu-icon" />
          <Typography variant="inherit">Insert Row Down</Typography>
        </MenuItem>
        <MenuItem onClick={() => { handleEditRowProps(currentRow); handleMenuClose(); }}>
          <EditIcon className="eform-menu-icon" style={{ color: "#007bff" }} />
          <Typography variant="inherit">Edit Row Properties</Typography>
        </MenuItem>
        <MenuItem onClick={() => { handleDeleteRow(currentRow); handleMenuClose(); }}>
          <RemoveIcon className="eform-menu-icon" />
          <Typography variant="inherit">Delete Row</Typography>
        </MenuItem>
      </Menu>)
      }
      {editControl &&
        <ControlEditor
          controlToEdit={editControl}
          data={gridContext}
          dataKey={control.dataKey}
          onComplete={() => { setEditControl(null) }}
          onError={() => setEditControl(null)} />}
      {editRule &&
        <RuleEditor
          ruleToEdit={editRule}
          data={gridContext}
          dataKey={control.dataKey}
          onComplete={handleEditRuleComplete}
          onError={() => setEditRule(null)} />}
      {editGridPosition &&
        <ValidationProvider rule={gridPositionEditorRule}>
          <ModalBox
            action={{
              type: 'messagebox',
              payload: {
                title: `Edit Grid Position (${editGridPosition.type})`,
                layout: gridPositionEditor,
                okbutton: true
              }
            }}
            data={editGridPosition.gridPosition}
            onDataChange={() => { }}
            mode="edit"
            onComplete={() => { setEditGridPosition(null) }}
            onError={() => { setEditGridPosition(null) }}
            onClose={() => { setEditGridPosition(null) }}
            sx={{ minWidth: '300px' }}
          />
        </ValidationProvider>}
      {editColumnProps &&
        <ValidationProvider rule={gridColumnEditorRule}>
          <ModalBox
            action={{
              type: 'messagebox',
              payload: {
                title: `Edit Column Properties (Column ${currentColumn + 1})`,
                layout: gridColumnEditor,
                okbutton: true
              }
            }}
            data={editColumnProps}
            onDataChange={() => { }}
            mode="edit"
            onComplete={() => { setEditColumnProps(null) }}
            onError={() => { setEditColumnProps(null) }}
            onClose={() => { setEditColumnProps(null) }}
            sx={{ minWidth: '300px' }}
          />
        </ValidationProvider>}
      {editRowProps &&
        <ValidationProvider rule={gridRowEditorRule}>
          <ModalBox
            action={{
              type: 'messagebox',
              payload: {
                title: `Edit Row Properties (Row ${currentRow + 1})`,
                layout: gridRowEditor,
                okbutton: true
              }
            }}
            data={editRowProps}
            onDataChange={() => { }}
            mode="edit"
            onComplete={() => { setEditRowProps(null) }}
            onError={() => { setEditRowProps(null) }}
            onClose={() => { setEditRowProps(null) }}
            sx={{ minWidth: '300px' }}
          />
        </ValidationProvider>}
    </>
  );
};

export default GridControl;
